/**
 * Snapshot offline: productos, tasa y tienda en IndexedDB.
 * Permite seguir vendiendo con datos de la última sesión online.
 */

const DB_NAME = 'poskn_snapshot_v1';
const STORE = 'meta';
const SNAPSHOT_KEY = 'catalog';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

function openDb() {
	return new Promise((resolve, reject) => {
		if (!('indexedDB' in window)) {
			reject(new Error('IndexedDB no disponible'));
			return;
		}
		const req = indexedDB.open(DB_NAME, 1);
		req.onupgradeneeded = () => {
			const db = req.result;
			if (!db.objectStoreNames.contains(STORE)) {
				db.createObjectStore(STORE);
			}
		};
		req.onsuccess = () => resolve(req.result);
		req.onerror = () => reject(req.error || new Error('No se pudo abrir snapshot DB'));
	});
}

async function idbGet(key) {
	const db = await openDb();
	return new Promise((resolve, reject) => {
		const tx = db.transaction(STORE, 'readonly');
		const req = tx.objectStore(STORE).get(key);
		req.onsuccess = () => resolve(req.result || null);
		req.onerror = () => reject(req.error);
	});
}

async function idbSet(key, value) {
	const db = await openDb();
	return new Promise((resolve, reject) => {
		const tx = db.transaction(STORE, 'readwrite');
		tx.objectStore(STORE).put(value, key);
		tx.oncomplete = () => resolve(true);
		tx.onerror = () => reject(tx.error);
	});
}

function collectProductCards(win) {
	const cards = [];
	try {
		const nodes = win.document.querySelectorAll('.producto-card');
		nodes.forEach((el) => {
			const id = el.getAttribute('data-id') || el.getAttribute('data-product-id') || '';
			const name =
				el.getAttribute('data-name') ||
				(el.querySelector('.producto-nombre, .product-name') || {}).textContent ||
				'';
			const price = parseFloat(el.getAttribute('data-price') || el.getAttribute('data-precio') || '0') || 0;
			const stock = parseFloat(el.getAttribute('data-stock') || '0');
			if (id || name) {
				cards.push({
					id: String(id),
					name: String(name).trim(),
					price,
					stock: Number.isFinite(stock) ? stock : null,
				});
			}
		});
	} catch (e) {
		/* ignore */
	}
	return cards;
}

function buildSnapshot(win) {
	const productosData = win.productosData != null ? win.productosData : null;
	const cards = collectProductCards(win);
	let productCount = 0;
	if (Array.isArray(productosData)) {
		productosData.forEach((cat) => {
			if (cat && Array.isArray(cat.products)) productCount += cat.products.length;
		});
	}
	return {
		savedAt: Date.now(),
		tasa: parseFloat(win.TASA_DOLAR) || 1,
		tienda: win.posknTiendaSeleccionada || win.posknUserTienda || null,
		caja: win._posknCajaCache || null,
		tiendaStock: win.posknTiendaStock || null,
		productosData,
		cards,
		cardCount: cards.length,
		productCount,
		hasCatalog: !!(productosData || cards.length),
	};
}

export async function saveOfflineSnapshot(win) {
	const snap = buildSnapshot(win);
	if (!snap.hasCatalog && !(snap.tasa > 0)) {
		return { ok: false, reason: 'empty' };
	}
	try {
		await idbSet(SNAPSHOT_KEY, snap);
		win._posknSnapshotMeta = {
			savedAt: snap.savedAt,
			cardCount: snap.cardCount,
			productCount: snap.productCount || 0,
		};
		return { ok: true, snap };
	} catch (e) {
		return { ok: false, error: e.message || String(e) };
	}
}

export async function loadOfflineSnapshot(win) {
	try {
		const snap = await idbGet(SNAPSHOT_KEY);
		if (!snap) return null;
		win._posknSnapshotMeta = {
			savedAt: snap.savedAt,
			cardCount: snap.cardCount || 0,
			productCount: snap.productCount || 0,
		};
		return snap;
	} catch (e) {
		return null;
	}
}

export function snapshotIsFresh(snap, maxAgeMs = MAX_AGE_MS) {
	if (!snap || !snap.savedAt) return false;
	return Date.now() - snap.savedAt < maxAgeMs;
}

export function applySnapshotToRuntime(win, snap) {
	if (!snap) return false;
	if (snap.tasa > 0) {
		win.TASA_DOLAR = snap.tasa;
		const el = win.document.getElementById('poskn-tasa-value');
		if (el) {
			el.textContent = Number(snap.tasa).toLocaleString('es-VE', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
		}
	}
	if (snap.productosData != null) {
		const currentEmpty = !Array.isArray(win.productosData) || !win.productosData.length;
		if (currentEmpty || !win.navigator.onLine) {
			win.productosData = snap.productosData;
			if (typeof win.posknApplyPosCatalog === 'function') {
				try {
					win.posknApplyPosCatalog(snap.productosData);
				} catch (e) {
					/* grid puede no estar listo */
				}
			}
		}
	}
	if (snap.tiendaStock && typeof snap.tiendaStock === 'object') {
		win.posknTiendaStock = snap.tiendaStock;
		win._posknTiendaStockReady = Object.keys(snap.tiendaStock).length > 0;
		if (snap.tienda && snap.tienda.id != null) {
			win._posknTiendaStockId = parseInt(snap.tienda.id, 10) || 0;
		}
	}
	if (snap.tienda && !win.posknTiendaSeleccionada) {
		win.posknTiendaSeleccionada = snap.tienda;
	}
	win._posknLastSnapshot = snap;
	return true;
}

/**
 * ¿Hay catálogo usable en pantalla o en snapshot?
 */
export function hasUsableCatalog(win) {
	if (win.document.querySelectorAll('.producto-card').length > 0) return true;
	if (Array.isArray(win.productosData) && win.productosData.length) return true;
	const snap = win._posknLastSnapshot;
	if (snap && snap.hasCatalog && snapshotIsFresh(snap)) return true;
	return false;
}

export function initOfflineSnapshot(win, $) {
	if (win._posknSnapshotBound) return;
	win._posknSnapshotBound = true;

	win.posknSaveOfflineSnapshot = () => saveOfflineSnapshot(win);
	win.posknLoadOfflineSnapshot = () => loadOfflineSnapshot(win);
	win.posknHasUsableCatalog = () => hasUsableCatalog(win);

	const persist = () => {
		saveOfflineSnapshot(win).then((r) => {
			if (r.ok && typeof win.posknUpdateConnectionStatus === 'function') {
				win.posknUpdateConnectionStatus();
			}
		});
	};

	$(() => {
		loadOfflineSnapshot(win).then((snap) => {
			if (snap) {
				applySnapshotToRuntime(win, snap);
				win.setTimeout(() => applySnapshotToRuntime(win, snap), 1400);
				if (!win.navigator.onLine && typeof win.mostrarNotificacion === 'function') {
					const ageH = Math.round((Date.now() - snap.savedAt) / 3600000);
					win.mostrarNotificacion(
						`Modo offline: catálogo/tasa de hace ~${ageH}h. Los pedidos irán a cola (hasta 500).`,
						'warning',
					);
				}
			}
			// Guardar snapshot tras pintar productos
			win.setTimeout(persist, 1500);
			win.setTimeout(persist, 8000);
		});
	});

	win.addEventListener('online', () => {
		win.setTimeout(persist, 2000);
	});

	// Refresco periódico mientras hay sesión
	win.setInterval(() => {
		if (win.navigator.onLine) persist();
	}, 5 * 60 * 1000);
}
